/**
 * RCD — Maturity instruction editor.
 *
 * v3.0 behavior (per modules digest §RCD):
 *   - Default: Core Director auto-renews at same term / current rate.
 *   - Per-customer maturity instructions NOT captured at open time in v3.0
 *     (deferred to v3.x — awaiting Scott/Amy confirmation on Core field).
 *   - This surface is READ-MOSTLY and displays the Core auto-renew config
 *     hydrated by `useCdDetail` (live AcctInq dual-call, not fixture).
 *
 * On save it emits a SessionEvent via `apiClient.retail.events.write({cd-maturity-instruction-changed})`.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2, Info } from 'lucide-react';
import { Button, Pill, WidgetCard, KVPair, CardRadioGroup } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocalDateTime } from '@/utils/time';
import { useCdDetail } from './hooks/useCdDetail';
import { formatMoney, formatShortDate } from './utils/cdFormat';
import './rcd.css';

type RenewalInstruction = 'auto-same-term' | 'auto-different-term' | 'redeem-to-dda' | 'hold-in-grace';

export function MaturityViewer(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const [instruction, setInstruction] = useState<RenewalInstruction>('auto-same-term');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live CD detail from the AcctInq dual-call (see `services/CdInqService.ts`).
  // Used to echo the Core renewal config back to the officer as a read-only
  // reference — the save action still only writes a SessionEvent in v3.0.
  const { data: cdDetail, loading: cdLoading, error: cdError } = useCdDetail(acctId);

  const save = async (): Promise<void> => {
    if (!acctId || !user) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.retail.events.write({
        eventType: 'cd-maturity-instruction-changed',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user.upn,
        payloadJson: JSON.stringify({
          acctId,
          acctType: '30',
          instruction,
          notes,
          coreRenewalCode: cdDetail?.renewalCode ?? null,
          coreAutoRenew: cdDetail?.autoRenew ?? null,
          maturityDate: cdDetail?.maturityDate ?? null,
          termMonths: cdDetail?.termMonths ?? null,
        }),
      });
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save instruction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rcd-detail">
      <div className="rcd-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to CD
        </Button>
        <h1>Maturity instructions · {acctId}</h1>
      </div>

      <div className="rcd-banner rcd-banner--info" role="note">
        <Info size={16} className="rcd-inline-icon" />
        <div>
          <strong>Core Director handles renewal automatically.</strong> Per-customer maturity
          instructions are a future enhancement (awaiting confirmation that Core Director exposes the
          field natively). Current selection is logged as a SessionEvent for audit, but does not
          override Core behavior.
        </div>
      </div>

      {cdError && (
        <div className="rcd-banner rcd-banner--err" role="alert">
          Unable to load CD detail from Core Director — {cdError}
        </div>
      )}

      <WidgetCard title="Current Core Director renewal config" stripe="pipeline">
        <div className="rcd-detail__kvgrid">
          <KVPair
            label="Auto-renew"
            value={
              cdLoading
                ? '…'
                : cdDetail?.autoRenew === true
                  ? 'Yes'
                  : cdDetail?.autoRenew === false
                    ? 'No'
                    : '—'
            }
          />
          <KVPair label="Renewal code" value={cdDetail?.renewalCode ?? (cdLoading ? '…' : '—')} />
          <KVPair
            label="Maturity date"
            value={cdDetail?.maturityDate ? formatShortDate(cdDetail.maturityDate) : (cdLoading ? '…' : '—')}
          />
          <KVPair
            label="Term"
            value={cdDetail?.termMonths == null ? (cdLoading ? '…' : '—') : `${cdDetail.termMonths} months`}
          />
          <KVPair
            label="Current rate"
            value={cdDetail?.currentRate == null ? (cdLoading ? '…' : '—') : `${cdDetail.currentRate.toFixed(2)}%`}
          />
          <KVPair
            label="Balance"
            value={cdDetail?.balance == null ? (cdLoading ? '…' : '—') : formatMoney(cdDetail.balance)}
          />
        </div>
      </WidgetCard>

      <WidgetCard title="Renewal instruction">
        <CardRadioGroup<RenewalInstruction>
          name="rcd-maturity-instruction"
          selected={instruction}
          onChange={setInstruction}
          options={[
            {
              value: 'auto-same-term',
              primary: 'Auto-renew · same term',
              helper: 'Default Core Director behavior. Current rate applied.',
            },
            {
              value: 'auto-different-term',
              primary: 'Auto-renew · different term',
              helper: 'Requires Scott/Amy confirmation; deferred.',
            },
            {
              value: 'redeem-to-dda',
              primary: 'Redeem to DDA at maturity',
              helper: 'Funds swept to customer DDA on maturity date.',
            },
            {
              value: 'hold-in-grace',
              primary: 'Hold in grace (contact customer)',
              helper: '10-day grace; officer calls customer for direction.',
            },
          ]}
        />
      </WidgetCard>

      <WidgetCard title="Officer notes (audit)">
        <div className="rcd-wizard__field">
          <label htmlFor="rcd-maturity-notes">Notes</label>
          <textarea
            id="rcd-maturity-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — e.g. 'Customer called and requested auto-renew for 12 months.'"
          />
        </div>
      </WidgetCard>

      {error && (
        <div className="rcd-banner rcd-banner--err" role="alert">
          {error}
        </div>
      )}

      <div className="rcd-wizard__footer">
        <div>
          <KVPair
            label="Last saved"
            value={savedAt ? formatLocalDateTime(savedAt) : '—'}
          />
        </div>
        <div className="rcd-detail__actions">
          {savedAt && (
            <Pill variant="ok" icon={<CheckCircle2 size={12} />}>
              Audit event logged
            </Pill>
          )}
          <Button variant="primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save instruction'}
          </Button>
        </div>
      </div>
    </div>
  );
}
